import { createAsyncThunk } from '@reduxjs/toolkit';

import {
  setEntityOnboardingTooltip,
  setOnboardingSetupProcess,
} from '@pages/common/Onboarding/store/onboarding-slice';
import api from '@setup/api';
import i18next from '@setup/locales/i18n';
import { NotificationService } from '@setup/notifications/SnackbarUtilsConfigurator/SnackbarUtilsConfigurator';

import type {
  WelcomeStepsType,
  HelpCentreLinks,
  OnboardingRoot,
} from '@pages/common/Onboarding/store/onboarding-types';
import type { EntityOnboardingTooltips } from '@pages/common/OnboardingTooltip/const/entity-onboarding-tooltips';
import type { TooltipsStatusesEnum } from '@pages/common/OnboardingTooltip/const/tooltips-statuses.enum';
import type { ErrorType } from '@shared/interfaces/error.interfaces';

export const getOnboardingSetup = createAsyncThunk<OnboardingRoot, any, { rejectValue: ErrorType }>(
  'getOnboardingSetup',
  async (_, { rejectWithValue }) => {
    try {
      return await api.onboardingSetup.getOnboardingSetup();
    } catch (e: any) {
      if (e?.status !== 422) {
        NotificationService.error(i18next.t('general.notifications.defaultError'));
      }
      return rejectWithValue(e?.data as ErrorType);
    }
  },
);

export const getWelcomeSteps = createAsyncThunk<WelcomeStepsType, any, { rejectValue: ErrorType }>(
  'getWelcomeSteps',
  async (_, { rejectWithValue }) => {
    try {
      return await api.onboardingSetup.getWelcomeSteps();
    } catch (e: any) {
      if (e?.status !== 422) {
        NotificationService.error(i18next.t('general.notifications.defaultError'));
      }
      return rejectWithValue(e?.data as ErrorType);
    }
  },
);

export const skipWelcomeSteps = createAsyncThunk<any, { key: string }, { rejectValue: ErrorType }>(
  'skipWelcomeSteps',
  async (skipData, { rejectWithValue }) => {
    try {
      return await api.onboardingSetup.skipWelcomeSteps(skipData);
    } catch (e: any) {
      if (e?.status !== 422) {
        NotificationService.error(i18next.t('general.notifications.defaultError'));
      }
      return rejectWithValue(e?.data as ErrorType);
    }
  },
);

export const getOnboardingHelpCentre = createAsyncThunk<HelpCentreLinks[], any, { rejectValue: ErrorType }>(
  'getOnboardingHelpCentre',
  async (_, { rejectWithValue }) => {
    try {
      return await api.onboardingSetup.getOnboardingHelpCenterLinks();
    } catch (e: any) {
      if (e?.status !== 422) {
        NotificationService.error(i18next.t('general.notifications.defaultError'));
      }
      return rejectWithValue(e?.data as ErrorType);
    }
  },
);

export const getOnboardingSetupProcess = createAsyncThunk<any, any, { rejectValue: ErrorType }>(
  'getOnboardingSetupProcess',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      await api.onboardingSetup
        .getOnboardingSetupProcess()
        .then((data) => {
          dispatch(setOnboardingSetupProcess({ process: data.process }));
        })
        .catch(() => {});
    } catch (e: any) {
      if (e?.status !== 422) {
        NotificationService.error(i18next.t('general.notifications.defaultError'));
      }
      return rejectWithValue(e?.data as ErrorType);
    }
  },
);

// -------------------- onboarding-tooltips

export const getOnboardingTooltips = createAsyncThunk<any, undefined, { rejectValue: ErrorType }>(
  'getOnboardingTooltips',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      await api.onboardingSetup
        .getOnboardingTooltips()
        .then((data) => {
          dispatch(setEntityOnboardingTooltip({ tooltips: data }));
        })
        .catch(() => {});
    } catch (e: any) {
      if (e?.status !== 422) {
        NotificationService.error(i18next.t('general.notifications.defaultError'));
      }
      return rejectWithValue(e?.data as ErrorType);
    }
  },
);

export const updateOnboardingTooltips = createAsyncThunk<
  undefined,
  { entity: EntityOnboardingTooltips; status: TooltipsStatusesEnum },
  { rejectValue: ErrorType }
>('updateOnboardingTooltips', async ({ entity, status }, { rejectWithValue }) => {
  try {
    await api.onboardingSetup.updateOnboardingTooltips({
      platform: 'web',
      area: entity,
      status,
      data: {},
    });
  } catch (e: any) {
    if (e?.status !== 422) {
      NotificationService.error(i18next.t('general.notifications.defaultError'));
    }
    return rejectWithValue(e.data as ErrorType);
  }
});
